import { NextSeo, NextSeoProps } from "next-seo";
import { useRouter } from "next/router";

import { RouteName } from "@/consts/RouteName";
import { PostWriterAvatarSize } from "@/consts/avatarSizes";
import { ErrorState } from "@/features/common/components/informationContainer";
import { DetailPostPlaceholder } from "@/features/post/components/DetailPostPlaceholder";
import { Eth721PostContent } from "@/features/post/components/Eth721PostContent";
import NFTPostContent from "@/features/post/components/NFTPostContent";
import { SensitiveContentDialog } from "@/features/post/components/SensitiveContentDialog";
import { NormalPostDetail } from "@/features/post/components/postDetail/NormalPostDetail";
import { PostTypeEnum, usePostQuery } from "@/generated/generated-hooks";
import { convertWhitespaceToPlusInSlug } from "@/helpers/common/convertWhitespaceToPlusInSlug";
import { getAbsolutePostPath } from "@/helpers/common/path";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import { noIndexPost } from "@/helpers/seo/getRobotsIndex";

const config = getPublicConfig();

/**
 * @purpose serve as middle layer to render PostContent based on postType
 * @return conditionally rendered %PostContent Component
 */
export const PostDetail = () => {
  // 1. get the target post
  const router = useRouter();
  const normalizedSlug = convertWhitespaceToPlusInSlug(
    (router.query.preview as string) ?? (router.query.slug as string),
  );

  const { data, loading, error } = usePostQuery({
    variables: {
      slug: normalizedSlug,
      avatarSize: {
        maxWidth: PostWriterAvatarSize.MaxWidth,
        maxHeight: PostWriterAvatarSize.MaxHeight,
      },
      isEdit: false,
    },
    skip:
      !normalizedSlug ||
      (!router.query.preview && router.route !== RouteName.PostRoute),
  });

  // 1b) handle the loading with placeholder
  if (!data || loading) return <DetailPostPlaceholder />;
  // 1c) handle the error
  if (error) return <ErrorState message={error.message ?? null} />;
  // 1d) return error if data dont contains post
  if (!data?.post) {
    router.replace("/404", `${RouteName.Post}/${normalizedSlug}`);
    return null;
  }

  // 2. check whether the postType is Normal or NFT, and render either NormalPostContent or NFTPostContent Component
  const { post } = data;
  const getSeoDescription = () => {
    const containTags = post?.tags.length > 0;
    const tagDescription = containTags
      ? post?.tags.map((tag) => tag.label).join(",")
      : "";
    return (post?.previewContent ?? "") + " | " + tagDescription;
  };

  const description = getSeoDescription();
  const noindex = noIndexPost(post);
  const canonical = getAbsolutePostPath(post.slug);

  const nextSeoProps: NextSeoProps = {
    title: `${post.title} | ${config.siteName}`,
    description,
    noindex,
    canonical,
    openGraph: {
      type: "website",
      title: `${post.title} | ${config.siteName}`,
      description,
      images: [
        {
          url: post.previewImage?.url ?? "/favicon.ico",
          width: 150,
          height: 150,
          alt: post.title,
        },
      ],
    },
  };

  return (
    <>
      <NextSeo {...nextSeoProps} />
      <SensitiveContentDialog post={post} />

      {post.postType === PostTypeEnum.Nft ? (
        <NFTPostContent post={post} />
      ) : post.postType === PostTypeEnum.Eth721 ? (
        <Eth721PostContent post={post} />
      ) : (
        <NormalPostDetail post={post} />
      )}
    </>
  );
};
